import { forwardRef, useImperativeHandle, useState } from "react";
import { Form } from "react-bootstrap";
import { CustomTypeSelection } from "../molecules/CustomTypeSelection";
import { userTypes, generalFocus } from "../../constants/verificationFormData";
import { useSellerForm } from "../../hooks/useSellerForm";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";

interface SellerFormProps {
  serverErrors?: any;
}

export const SellerForm = forwardRef<{ submitForm: () => any }, SellerFormProps>(({ serverErrors }, ref) => {
  const { formData, handleInputChange, handleCheckboxChange, submitForm, errors, getFieldError } = useSellerForm({ serverErrors });
  useImperativeHandle(ref, () => ({
    submitForm,
  }));

  return (
    <Form>
      <div className="mb-3">
        <label htmlFor="institutionName" className="form-label" style={{ color: '#ffffff' }}>Name</label>
        <input
          id="institutionName"
          name="institutionName"
          value={formData.institutionName}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('institutionName') ? 'is-invalid' : ''}`}
        />
        {getFieldError('institutionName') && (
          <div className="text-danger small mt-1">
            {getFieldError('institutionName')}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="shortBio" className="form-label" style={{ color: '#ffffff' }}>Short Bio</label>
        <textarea
          id="shortBio"
          name="shortBio"
          value={formData.shortBio}
          onChange={handleInputChange}
          rows={4}
          className={`form-control ${getFieldError('shortBio') ? 'is-invalid' : ''}`}
        />
        {getFieldError('shortBio') && (
          <div className="text-danger small mt-1">
            {getFieldError('shortBio')}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="links.websiteLink" className="form-label" style={{ color: '#ffffff' }}>Website URL</label>
        <input
          id="links.websiteLink"
          name="links.websiteLink"
          value={formData.links.websiteLink}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('websiteLink') ? 'is-invalid' : ''}`}
        />
        {getFieldError('websiteLink') && (
          <div className="text-danger small mt-1">
            {getFieldError('websiteLink')}
          </div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="links.portfolioLink" className="form-label" style={{ color: '#ffffff' }}>Portfolio URL</label>
        <input
          id="links.portfolioLink"
          name="links.portfolioLink"
          value={formData.links.portfolioLink}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('portfolioLink') ? 'is-invalid' : ''}`}
        />
        {getFieldError('portfolioLink') && (
          <div className="text-danger small mt-1">
            {getFieldError('portfolioLink')}
          </div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="links.twitterLink" className="form-label" style={{ color: '#ffffff' }}>Twitter Profile URL</label>
        <input
          id="links.twitterLink"
          name="links.twitterLink"
          value={formData.links.twitterLink}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('twitterLink') ? 'is-invalid' : ''}`}
        />
        {getFieldError('twitterLink') && (
          <div className="text-danger small mt-1">
            {getFieldError('twitterLink')}
          </div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="links.facebookLink" className="form-label" style={{ color: '#ffffff' }}>Facebook Profile URL</label>
        <input
          id="links.facebookLink"
          name="links.facebookLink"
          value={formData.links.facebookLink}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('facebookLink') ? 'is-invalid' : ''}`}
        />
        {getFieldError('facebookLink') && (
          <div className="text-danger small mt-1">
            {getFieldError('facebookLink')}
          </div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="links.instagramLink" className="form-label" style={{ color: '#ffffff' }}>Instagram URL</label>
        <input
          id="links.instagramLink"
          name="links.instagramLink"
          value={formData.links.instagramLink}
          onChange={handleInputChange}
          className={`form-control ${getFieldError('instagramLink') ? 'is-invalid' : ''}`}
        />
        {getFieldError('instagramLink') && (
          <div className="text-danger small mt-1">
            {getFieldError('instagramLink')}
          </div>
        )}
        {getFieldError('links') && (
          <div className="text-danger small mt-1">
            {getFieldError('links')}
          </div>
        )}
      </div>

      <div className="mb-3">
        <CustomTypeSelection 
          label="Type of user"
          options={userTypes}
          selected={formData.typeOfUser}
          onChange={(val) => handleCheckboxChange("typeOfUser", val)}
        />
        {getFieldError('typeOfUser') && (
          <div className="text-danger small mt-1">
            {getFieldError('typeOfUser')}
          </div>
        )}
      </div>

      <div className="mb-3">
        <CustomTypeSelection 
          label="What is your general focus"
          options={generalFocus}
          selected={formData.generalFocus}
          onChange={(val) => handleCheckboxChange("generalFocus", val)}
        />
        {getFieldError('generalFocus') && (
          <div className="text-danger small mt-1">
            {getFieldError('generalFocus')}
          </div>
        )}
      </div>

        <div className="mb-3 d-flex text-white">
          <input
            type="checkbox"
            id="termsAndConditions"
            name="termsAndConditions"
            className="form-check-input me-2"
            onChange={handleInputChange}
          />
          <label htmlFor="termsAndConditions" className="mb-0 d-inline">
            I have read and agree to the{" "}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white text-decoration-none fst-italic fs-6 fw-bold"
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              Terms and Conditions <FontAwesomeIcon icon={faExternalLink} className="ms-1" />
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white text-decoration-none fst-italic fs-6 fw-bold"
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              Privacy Policy <FontAwesomeIcon icon={faExternalLink} className="ms-1" />
            </Link>
          </label>
        </div>
        {getFieldError('termsAndConditions') && (
          <div className="text-danger small mt-1">
            {getFieldError('termsAndConditions')}
          </div>
        )}
    </Form>
  );
});
